// The ONLY module in this SPA that speaks HTTP (decomposition.md section 6.2's "api/ is the
// only module allowed to speak HTTP"). Every feature client (roadmap.ts, portfolioData.ts, ...)
// is built over the four verbs here, so the status-code table (D-204), the correlation header
// (Boundary/CallerContextSource.cs, X-Correlation-Id), the bearer token attachment and the
// ProblemDetails parsing exist in exactly one place.
import { ApiError, kindForStatus } from "./ApiError";
import { getApiConfig } from "./httpConfig";
import { isProblemDetailsBody } from "./problemDetails";

/** The header CallerContextSource reads a caller-supplied trace id from. */
export const CORRELATION_HEADER = "X-Correlation-Id";

function newCorrelationId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  // Fallback for a test/runtime environment with no Web Crypto UUID support.
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

export interface RequestOptions {
  readonly query?: Readonly<Record<string, string | number | boolean | readonly string[] | null | undefined>>;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /** Sends no Authorization header and never triggers onUnauthenticated - used only pre-sign-in. */
  readonly anonymous?: boolean;
}

function buildQuery(query: RequestOptions["query"]): string {
  if (!query) return "";

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, item);
    } else {
      params.append(key, String(value));
    }
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return null;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  const kind = kindForStatus(response.status);
  const parsed = await parseBody(response).catch(() => null);
  const problem = isProblemDetailsBody(parsed) ? parsed : undefined;
  const message = problem?.detail ?? problem?.title ?? `Request failed with status ${response.status}.`;
  return new ApiError(kind, response.status, message, problem);
}

async function send<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const config = getApiConfig();
  const url = `${config.baseUrl}${path}${buildQuery(options.query)}`;

  const headers: Record<string, string> = {
    [CORRELATION_HEADER]: newCorrelationId(),
  };

  if (!options.anonymous) {
    const token = await config.getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  let requestBody: BodyInit | undefined;
  if (options.body instanceof FormData) {
    requestBody = options.body;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    requestBody = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: requestBody,
      signal: options.signal,
      credentials: "same-origin",
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") {
      throw new ApiError("cancelled", 499, "The request was withdrawn by the caller.");
    }

    throw new ApiError(
      "network",
      0,
      "The request could not reach the Evergreen Portfolio API.",
      undefined,
    );
  }

  if (!response.ok) {
    const error = await toApiError(response);
    if (error.kind === "unauthenticated" && !options.anonymous) {
      config.onUnauthenticated();
    }
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await parseBody(response)) as T;
}

export const http = {
  get: <T>(path: string, options?: RequestOptions) => send<T>("GET", path, options),
  post: <T>(path: string, options?: RequestOptions) => send<T>("POST", path, options),
  put: <T>(path: string, options?: RequestOptions) => send<T>("PUT", path, options),
  delete: <T>(path: string, options?: RequestOptions) => send<T>("DELETE", path, options),
};

/**
 * Downloads a binary artifact (GET /api/artifacts/{key}) as a Blob rather than JSON - the one
 * shape ArtifactsController returns that is not a DTO (File(artifact.Content, ...)).
 */
export async function httpDownload(path: string, options: RequestOptions = {}): Promise<Blob> {
  const config = getApiConfig();
  const url = `${config.baseUrl}${path}${buildQuery(options.query)}`;
  const headers: Record<string, string> = { [CORRELATION_HEADER]: newCorrelationId() };

  const token = await config.getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(url, { method: "GET", headers, signal: options.signal });
  if (!response.ok) {
    const error = await toApiError(response);
    if (error.kind === "unauthenticated") config.onUnauthenticated();
    throw error;
  }

  return response.blob();
}
